import React, { useState, useEffect, useMemo } from "react";
import { notification } from "antd";
import { FinetuningService } from "../../services/FinetuningService";
import { usePermissions } from "../../PermissionsProvider";
import { Model } from "../../model_gen/model";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { PlusIcon } from "lucide-react";
import ModelsTable from "../shared/ModelsTable";

const Models: React.FC = () => {
  const { apiKey } = usePermissions();
  const [modelData, setModelData] = useState<Model[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const navigate = useNavigate();

  const fetchData = () => {
    FinetuningService.getFinetunedModels(setIsLoading, apiKey).subscribe(
      (data: Model[]) => {
        setModelData(data);
      },
      (error: Error) => {
        console.error("Error fetching model data:", error);
        notification.error({
          message: "Error",
          description: "Failed to load models",
        });
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [apiKey]);

  // Group models by model name, then flatten for pagination
  const processedData = useMemo(() => {
    const groups = modelData.reduce(
      (acc: { [key: string]: Model[] }, model) => {
        if (!acc[model.model]) {
          acc[model.model] = [];
        }
        acc[model.model].push(model);
        return acc;
      },
      {}
    );

    // Flatten grouped models - if multiple models with same name, show them separately
    return Object.entries(groups).flatMap(([modelName, models]) => {
      return models.map((model) => ({
        ...model,
        key: model.id, // Use unique ID as key
      }));
    });
  }, [modelData]);

  // Pagination logic
  const totalItems = processedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentPageData = processedData.slice(startIndex, startIndex + pageSize);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/build")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Build
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Models</span>
                </li>
              </ol>
            </nav>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Models
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manage your fine-tuned models
                </p>
              </div>
              <Button
                onClick={() => navigate("/build/finetuning/new")}
                className="gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                New Model
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <ModelsTable
          models={currentPageData}
          loading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
          emptyTitle="No fine-tuned models found"
          emptyDescription="Create a new fine-tuned model by starting a finetune run."
        />
      </div>
    </div>
  );
};

export default Models;
